import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { ObjectTarget } from "../../app/permissions";
import { getToken } from "../../features/user/UserAvatar";

// Survey Types - corresponds to SurveyType.java
export enum SurveyType {
    /**
     * Send a message to the group of the target and let
     * the users to vote on it.
     */
    GROUP = 0,
    /**
     * Send a message to each student in the private conversation
     * for the target and allow only one answer per student.
     */
    PRIVATE_PER_STUDENT = 1,
    /**
     * Send a message to each user in the private conversation
     * for the target and allow one answer per user, no matter if
     * that repeats the student.
     */
    PRIVATE_PER_USER = 2,
}

// Survey Question Types - corresponds to SurveyQuestionType.java
export enum SurveyQuestionType {
    /**
     * List of options to choose from.
     */
    OPTIONS = 0,
}

// Survey Status - corresponds to SurveyStatus.java  
export enum SurveyStatus {
    NOT_STARTED = 0,
    IN_PROGRESS = 1,
    COMPLETED = 2,
}

export const getSurveyStatusName = (status: SurveyStatus): string => {
    switch (status) {
        case SurveyStatus.NOT_STARTED:
            return "Não iniciado";
        case SurveyStatus.IN_PROGRESS:
            return "Em progresso";
        case SurveyStatus.COMPLETED:
            return "Finalizada";
        default:
            return "Desconhecido";
    }
};

export interface SurveyState {
    state: LoadStateInfo;
    request?: RetrieveSurveyRequest;
    selected?: SurveyInfo;
}

const initialState: SurveyState = {
    state: notStarted(),
};

export interface RetrieveSurveyRequest {
    surveyId: string;
}

export const retrieveSurvey = createAsyncThunk(
    "retrieveSurvey",
    async (param: RetrieveSurveyRequest, thunkAPI) => {
        console.debug("retrieve survey");

        const response = await fetch(`${getBackendUrl()}/api/survey/get/${param.surveyId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveSurveyRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.survey;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveSurvey skipped: ${status.state.state}`);
            return false;
        }
    }
);

export const surveySlice = createSlice({
    name: "survey",
    initialState,
    reducers: {
        select: (state, action: PayloadAction<SurveyInfo | undefined>) => {
            console.trace(`select :: ${action.payload?.id}`);
            state.selected = action.payload;
        },
        check: (state, action: PayloadAction<string | undefined>) => {
            if (state.state.state != LoadState.NotStarted
                && (action.payload == undefined || state.request?.surveyId != action.payload)
            ) {
                state.request = undefined;
                state.state = notStarted();
                state.selected = undefined;
            }
        },
        clean: (state) => {
            state.request = undefined;
            state.selected = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveSurvey.pending, (state, action) => {
                state.request = action.meta.arg;
                state.state = loading();
                state.selected = undefined;
            })
            .addCase(retrieveSurvey.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.selected = action.payload;
            })
            .addCase(retrieveSurvey.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export interface SurveyInfo {
    id: string;
    schoolId: string;
    type: SurveyType;
    target: ObjectTarget;
    targetId?: string;
    status: SurveyStatus;
    startAt?: string;
    endAt?: string;
    questions?: SurveyQuestionInfo[];
}

export interface SurveyQuestionInfo {
    id: string;
    title: string;
    description: string;
    type: SurveyQuestionType;
    options?: SurveyQuestionOptionInfo[];
}

export interface SurveyQuestionOptionInfo {
    id: string;
    title: string;
    description?: string;
}

export const { select, check, clean } = surveySlice.actions;

export const selectSurvey = (state: RootState) => state.survey;

export default surveySlice.reducer;
